/**
 * Shared with me, and shared by me.
 *
 * Both follow the prototype's tables. The pending-invite panel is the one part
 * with behaviour the design could not have anticipated: accepting an invitation
 * does *not* make it readable on its own, because the key still has to be
 * wrapped to this account's public key by the owner's browser. The panel says
 * so rather than showing an item that opens to nothing.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { api, errorText, type SharedWithMeWire } from '../api';
import { ds } from '../ds';
import {
  Avatar,
  DataRow,
  DataTable,
  Footnote,
  PageTitle,
  Panel,
  type Column
} from '../components/Shell';
import { useApp } from '../state';
import { useIsNarrow } from '../responsive';
import { Preview } from '../components/Preview';
import { downloadToDisk, getCachedKey, openSharedNode, type DecryptedNode } from '../nodes';
import { fileIcon, formatBytes, formatWhen, monoPlain, tint } from '../theme';
import { useT } from '../i18n';

interface Row {
  share: SharedWithMeWire;
  node: DecryptedNode | null;
}

export function SharedWithMe() {
  const t = useT();
  const { Button, Icon } = ds();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<DecryptedNode | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { shares } = await api.shares.withMe();
      // Decrypt each in turn. A share whose key will not open is shown as
      // unreadable rather than dropped — the recipient may need to chase it.
      const decrypted = await Promise.all(
        shares.map(async (share) => ({
          share,
          node: await openSharedNode(share).catch(() => null)
        }))
      );
      setRows(decrypted);
    } catch (err) {
      setError(errorText(err, 'Could not load your shares.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = rows.filter((r) => r.share.state === 'pending' || !r.share.nodeKeyWrapped);
  const accepted = rows.filter((r) => r.share.state === 'accepted' && r.share.nodeKeyWrapped);

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <PageTitle
        title={t('shared.shared-with-me')}
        meta={`${rows.length} item${rows.length === 1 ? '' : 's'} · ${pending.length} waiting`}
      />

      {error && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span>
        </Panel>
      )}

      {pending.length > 0 && (
        <Panel style={{ padding: '22px 26px 18px' }}>
          <h2
            style={{
              fontFamily: "var(--font-display), 'Baloo 2', system-ui",
              fontWeight: 800,
              fontSize: 22,
              margin: '0 0 4px',
              color: 'var(--text-strong)'
            }}
          >
            {t('shared.waiting-for-you')}
          </h2>
          <div style={{ ...monoPlain, marginBottom: 14 }}>
            {pending.length} invite{pending.length === 1 ? '' : 's'}
          </div>

          {pending.map(({ share }) => (
            <div
              key={share.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '11px 0',
                borderTop: '1px solid var(--border-subtle)'
              }}
            >
              <span style={typeTile(null)}>
                <Icon name={share.kind === 'folder' ? 'folder' : 'file-text'} size={19} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                  Something from {share.owner.name}
                </div>
                <div style={monoPlain}>
                  {share.owner.email}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  await api.shares.decline(share.id);
                  await load();
                }}
              >
                Decline
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={async () => {
                  await api.shares.accept(share.id);
                  await load();
                }}
              >
                Accept
              </Button>
            </div>
          ))}

          <Footnote>
            {t('shared.accepting-tells-the-owner-you')}
          </Footnote>
        </Panel>
      )}

      {loading ? (
        <Panel style={{ padding: 40 }}>
          <span style={monoPlain}>{t('shared.decrypting')}</span>
        </Panel>
      ) : (
        <DataTable
          columns={WITH_ME_COLUMNS}
          empty={
            accepted.length === 0 ? (
              <span style={monoPlain}>{t('shared.nothing-has-been-shared-with')}</span>
            ) : undefined
          }
        >
          {accepted.map(({ share, node }, i) => {
            const colours = tint(node?.mime ?? null);
            return (
              <DataRow
                key={share.id}
                columns={WITH_ME_COLUMNS}
                first={i === 0}
                onOpen={node && share.kind === 'file' ? () => setPreviewing(node) : undefined}
                tile={
                  <span style={{ ...typeTile(colours.bg), color: colours.fg }}>
                    <Icon name={fileIcon(share.kind, node?.mime ?? null)} size={19} />
                  </span>
                }
                cells={[
                  <>
                    <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                      {node?.name ?? 'Could not be read'}
                    </div>
                    <div style={monoPlain}>{formatBytes(share.plainSizeBytes)}</div>
                  </>,
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar name={share.owner.name} email={share.owner.email ?? ''} size={26} />
                    <span style={{ fontSize: 13.5, color: 'var(--text-body)' }}>
                      {share.owner.name}
                    </span>
                  </span>,
                  <span style={{ ...monoPlain, textTransform: 'none' }}>
                    {share.role === 'editor'
                      ? 'Can edit'
                      : share.role === 'commenter'
                        ? 'Can comment'
                        : 'Can view'}
                  </span>,
                  <span style={monoPlain}>{formatWhen(share.sharedAt)}</span>
                ]}
                actions={
                  node && share.kind === 'file' ? (
                    <button
                      aria-label={t('shared.download')}
                      onClick={async () => {
                        const key = getCachedKey(share.nodeId);
                        if (key) await downloadToDisk(node, key);
                      }}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 10,
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--text-muted)',
                        cursor: 'pointer'
                      }}
                    >
                      <Icon name="download" size={15} />
                    </button>
                  ) : undefined
                }
              />
            );
          })}
        </DataTable>
      )}

      {previewing && (
        <Preview
          node={previewing}
          nodeKey={getCachedKey(previewing.id)}
          onClose={() => setPreviewing(null)}
        />
      )}

    </div>
  );
}

const WITH_ME_COLUMNS: Column[] = [
  { width: 38 },
  { label: 'Name', width: 'flex' },
  { label: 'Owner', width: 168 },
  // The widest fixed set of any table, so it gives way earliest. Owner stays:
  // it is the column that says whose file this is.
  { label: 'Your access', width: 120, hideBelow: 820 },
  { label: 'Shared', width: 116, align: 'right', hideBelow: 680 },
  { width: 40 }
];

const BY_ME_COLUMNS: Column[] = [
  { label: 'File and link', width: 'flex' },
  { label: 'Access', width: 118 },
  { label: 'Protection', width: 150, hideBelow: 620 },
  { label: 'Opens', width: 88, align: 'right', hideBelow: 740 },
  { width: 60 }
];

const typeTile = (bg: string | null): React.CSSProperties => ({
  width: 38,
  height: 38,
  flex: '0 0 38px',
  borderRadius: 12,
  display: 'grid',
  placeItems: 'center',
  background: bg ?? 'var(--surface-sunken)',
  color: 'var(--text-muted)'
});

// ── shared by me ────────────────────────────────────────────────────────────

export function SharedByMe() {
  const t = useT();
  const { Button, Icon } = ds();
  const { completedShares } = useApp();
  const narrow = useIsNarrow();
  const [state, setState] = useState<Awaited<ReturnType<typeof api.links.mine>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await api.links.mine());
    } catch (err) {
      setError(errorText(err, 'Could not load your links.'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const stats = state?.stats;

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {/* The destructive button sits beside the title on a wide screen and
          under it on a phone, where sharing the line squeezed the heading to
          two wrapped words. */}
      <div
        style={{
          display: 'flex',
          alignItems: narrow ? 'stretch' : 'flex-end',
          flexDirection: narrow ? 'column' : 'row',
          justifyContent: 'space-between',
          gap: 16
        }}
      >
        <PageTitle
          title={t('shared.shared-by-me')}
          meta={
            stats
              ? `${stats.active} link${stats.active === 1 ? '' : 's'} · ${stats.withPassword} with a password · ${stats.expiringSoon} expiring`
              : undefined
          }
        />
        <Button
          variant="danger"
          size="md"
          onClick={async () => {
            if (!window.confirm('Revoke every link? People you invited by email keep access.'))
              return;
            await api.links.revokeAll();
            await load();
          }}
        >
          {t('shared.revoke-all-links')}
        </Button>
      </div>

      {error && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span>
        </Panel>
      )}

      {/* An invite sent to someone without a vault cannot carry a key. When they
          join, this browser wraps it — and says so, because from the owner's
          side the share had looked finished all along. */}
      {completedShares > 0 && (
        <Panel style={{ padding: '14px 20px', background: 'var(--primary-soft)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--primary-text)' }}>
            {completedShares} invitation{completedShares === 1 ? '' : 's'} finished. They can open
            what you shared now.
          </span>
        </Panel>
      )}

      {stats && (
        // Four fixed columns crushed each stat to about 70px on a phone. Auto-fit
        // drops to two, then one, without a breakpoint to maintain.
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 16
          }}
        >
          <Stat label={t('shared.active-links')} value={stats.active} />
          <Stat label={t('shared.with-a-password')} value={stats.withPassword} />
          <Stat label={t('shared.opens')} value={stats.opens} />
          <Stat label={t('shared.expiring-soon')} value={stats.expiringSoon} />
        </div>
      )}

      {!state ? (
        <Panel style={{ padding: 40 }}>
          <span style={monoPlain}>{t('shared.loading')}</span>
        </Panel>
      ) : (
        <DataTable
          columns={BY_ME_COLUMNS}
          empty={
            state.links.length === 0 ? (
              <span style={monoPlain}>{t('shared.you-have-not-shared-anything')}</span>
            ) : undefined
          }
        >
          {state.links.map((link, i) => (
            <DataRow
              key={link.id}
              columns={BY_ME_COLUMNS}
              first={i === 0}
              cells={[
                <>
                  {/* The name stays encrypted here: this list spans folders, so
                      the keys to decrypt each one are not all loaded. The link
                      id is what the owner recognises it by. */}
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                    {link.kind === 'folder' ? 'Folder' : 'File'} · {link.id.slice(-8)}
                  </div>
                  <div style={monoPlain}>created {formatWhen(link.createdAt)}</div>
                </>,
                <span style={{ ...monoPlain, textTransform: 'none' }}>
                  {link.role === 'editor'
                    ? 'Can edit'
                    : link.role === 'commenter'
                      ? 'Can comment'
                      : 'Can view'}
                </span>,
                <span style={{ ...monoPlain, textTransform: 'none' }}>
                  {link.hasPassword ? 'Password' : 'Link only'}
                  {link.expiresAt ? ` · expires ${formatWhen(link.expiresAt)}` : ''}
                </span>,
                <span style={monoPlain}>{link.viewCount}</span>
              ]}
              actions={
                <button
                  aria-label={t('shared.revoke')}
                  onClick={async () => {
                    await api.links.revoke(link.id);
                    await load();
                  }}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    cursor: 'pointer'
                  }}
                >
                  <Icon name="x" size={15} />
                </button>
              }
            />
          ))}
        </DataTable>
      )}

      <Footnote>
        {t('shared.revoking-a-link-stops-it')}
      </Footnote>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Panel style={{ padding: '20px 22px' }}>
      <div
        style={{
          fontFamily: "var(--font-display), 'Baloo 2', system-ui",
          fontWeight: 800,
          fontSize: 32,
          color: 'var(--text-strong)',
          lineHeight: 1.02
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono), monospace",
          fontSize: 10.5,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--text-faint)',
          marginTop: 6
        }}
      >
        {label}
      </div>
    </Panel>
  );
}
